import { ArrowUpRight, BookTextIcon, CheckIcon, ClockIcon } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { AssetWithStats } from '@/common/asset';
import { ROLE } from '@/common/role';
import { AssetIcon, InspectionIcon } from '@/components/entity-icons';
import { Restricted } from '@/components/Restricted';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermission } from '@/hooks/use-permission';
import { cn, formatCurrencyWithCents, formatDateOnly } from '@/lib/utils';
import { getConditionRatingColorsMapping } from '@/lib/utils/asset';
import { getMediaUrl } from '@/lib/utils/storage';
import { ImageWithFallback } from '../../../imageWithFallback';
import { AssetInactiveBadge, AssetOrganizationLabel } from '../AssetOrganizationLabel';
import { DamageStatBox } from '../DamageStatBox';

export type AssetListMapCardProps = {
	asset: AssetWithStats;
	onSelectAsset?: (asset: AssetWithStats | null) => void;
	selectedAssetId?: string | null;
	ref: React.Ref<HTMLDivElement>;
	/** Label the card with the asset's organization (used when listing all organizations) */
	showOrganization?: boolean;
};

export const AssetListMapCard = ({
	asset,
	onSelectAsset,
	selectedAssetId,
	ref,
	showOrganization = false,
}: AssetListMapCardProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const conditionRatingColorsMapping = getConditionRatingColorsMapping(asset.conditionRating || 0);
	const isSelected = selectedAssetId === asset.id;
	const hasInspectionManagementPagePermission = usePermission(ROLE.INSPECTION_MANAGEMENT_PAGE_VIEW);
	const hasWorkPackagesReadPermission = usePermission(ROLE.WORK_PACKAGES_READ);
	const hasSensorsReadPermission = usePermission(ROLE.SENSORS_READ);

	const handleSelect = () => {
		if (isSelected) {
			onSelectAsset?.(null);
		} else {
			onSelectAsset?.(asset);
		}
	};

	// Determine the best navigation option based on permissions
	const navigateObj = useMemo(() => {
		if (hasInspectionManagementPagePermission) {
			return { path: `/assets/${asset.id}`, label: t('assets.card.goToAsset') };
		}
		if (hasWorkPackagesReadPermission) {
			return { path: `/assets/${asset.id}/finance`, label: t('assets.card.viewFinance') };
		}
		if (hasSensorsReadPermission) {
			return { path: `/assets/${asset.id}/health`, label: t('assets.card.viewHealth') };
		}
		// If the user doesn't have permissions for any specific page, just navigate to the asset detail as a fallback (which will likely show an access denied message, but at least it's something).
		return { path: `/assets/${asset.id}`, label: t('assets.card.goToAsset') };
	}, [asset.id, hasInspectionManagementPagePermission, hasWorkPackagesReadPermission, hasSensorsReadPermission, t]);

	// This function is used for both the card click and the "Go to Asset" button, so that if a user clicks anywhere on the card, it will navigate to the appropriate page based on their permissions.
	const navigateToAsset = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation(); // Prevents the card click (map pan) from triggering when navigating.
			navigate(navigateObj.path);
		},
		[navigate, navigateObj.path],
	);

	const latestInspection = asset.latestInspection;
	const navigateToLatestInspection = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (latestInspection) navigate(`/assets/${asset.id}/inspections/${latestInspection.id}`);
	};

	const pendingCount = asset.financeStats?.Pending?.count || 0;
	const isPendingZero = pendingCount === 0;

	const assignedCount = asset.financeStats?.Assigned?.count || 0;
	const isAssignedZero = assignedCount === 0;

	return (
		<Card
			ref={ref}
			className={cn(
				'rounded-lg border border-border shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1 flex flex-col overflow-hidden cursor-pointer py-0 w-full gap-0',
				{
					'border-primary': isSelected,
				},
			)}
			onClick={handleSelect}
		>
			<CardHeader className="flex flex-col space-y-1.5 p-3 pb-3 relative">
				<div className="grid grid-cols-2 w-full gap-3">
					{/* Image on the left, details and buttons on the right */}
					<div className="flex-1 flex flex-col min-w-0 order-2">
						<div className="flex justify-between items-start gap-2 mb-1.5">
							{/* Name, Condition Rating, and Ongoing Inspection Icon */}
							<CardTitle className="text-base text-card-foreground leading-tight flex items-start gap-2">
								<AssetIcon
									className="size-4 shrink-0 mt-0.5 text-muted-foreground"
									aria-label={t('assets.card.iconLabel')}
								/>
								<span className="line-clamp-2">{asset.name}</span>
							</CardTitle>
							<AssetInactiveBadge asset={asset} />
						</div>
						{showOrganization && <AssetOrganizationLabel asset={asset} className="mb-1.5" />}

						<div className="flex items-start text-muted-foreground mb-2">
							<CardDescription className="text-xs">
								<span className="line-clamp-3">{asset.location || t('assets.card.noLocation')}</span>
								<div
									className="flex items-center gap-1.5 shrink-0 mt-0.5"
									title={
										asset.conditionRating
											? t('assets.card.conditionRatingTitle', { rating: asset.conditionRating })
											: t('assets.card.noConditionRating')
									}
								>
									<span
										className={cn(
											'text-xs font-bold px-2 py-0.5 rounded shadow-sm border',
											conditionRatingColorsMapping,
										)}
									>
										{(asset.conditionRating ?? 0).toFixed(1)}
									</span>
									{asset.hasOngoingInspection && (
										<div
											title={t('assets.card.ongoingInspection')}
											className="flex items-center justify-center bg-primary/10 border border-primary/20 text-primary h-6 w-6 rounded shadow-sm"
										>
											<BookTextIcon className="h-3.5 w-3.5" />
										</div>
									)}
								</div>
							</CardDescription>
						</div>

						{/* Go to Asset Detail + Last Inspection Buttons */}
						<div className="mt-auto flex flex-wrap justify-end gap-1.5">
							{latestInspection && (
								<Restricted to={ROLE.INSPECTIONS_READ}>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												variant="outline"
												size="sm"
												className="flex items-center px-2 py-1 rounded-md text-[11px] font-semibold shadow-sm w-fit cursor-pointer"
												onClick={navigateToLatestInspection}
											>
												<InspectionIcon className="h-3.5 w-3.5" /> {t('assets.list.lastInspection')}
											</Button>
										</TooltipTrigger>
										<TooltipContent>
											{t('assets.list.openLatestInspection', {
												title: `${latestInspection.title} (${formatDateOnly(latestInspection.startDate)})`,
											})}
										</TooltipContent>
									</Tooltip>
								</Restricted>
							)}
							<Button
								variant="outline"
								size="sm"
								className="flex items-center bg-primary/10 border border-primary/20 hover:text-primary/80 px-2 py-1 rounded-md text-[11px] font-semibold shadow-sm w-fit cursor-pointer text-primary"
								onClick={navigateToAsset}
							>
								{navigateObj.label} <ArrowUpRight className="h-3.5 w-3.5" />
							</Button>
						</div>
					</div>

					<div
						className="flex-1 relative group cursor-pointer overflow-hidden rounded-lg min-h-[100px] order-1"
						onClick={navigateToAsset}
					>
						<ImageWithFallback
							src={getMediaUrl(asset.imageUrl)}
							alt={asset.name}
							className={cn(
								'h-full w-full object-cover transition-transform duration-300',
								isSelected ? 'scale-105' : 'group-hover:scale-105',
							)}
						/>

						{/* Overlay Image Go to Asset Detail */}
						<div
							className={cn(
								'absolute inset-0 bg-background/60 flex flex-col items-center justify-center transition-opacity duration-200',
								isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
							)}
						>
							<ArrowUpRight className="text-primary-foreground size-6 mb-1" />
							<span className="text-primary-foreground text-xs font-semibold px-2 text-center">
								{navigateObj.label}
							</span>
						</div>
					</div>
				</div>
			</CardHeader>

			<CardContent className="flex-1 px-3 pb-3 pt-0">
				<Restricted to={ROLE.WORK_PACKAGES_READ}>
					<div className="grid grid-cols-2 gap-3 mb-1">
						{/* Pending Work Packages */}
						<div className="flex flex-col p-2.5 bg-card rounded-lg border border-border shadow-sm relative overflow-hidden group">
							<div
								className={cn(
									'absolute left-0 top-0 bottom-0 w-1',
									isPendingZero ? 'bg-muted-foreground/30' : 'bg-primary',
								)}
							/>
							<div className="pl-1.5 flex flex-col">
								<div className="flex justify-between items-center mb-1">
									<span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider flex items-center">
										<ClockIcon
											className={cn('h-3 w-3 mr-1', isPendingZero ? 'text-muted-foreground/50' : 'text-primary')}
										/>{' '}
										{t('assets.card.pendingWP')}
									</span>
								</div>
								<span
									className={cn(
										'text-sm font-extrabold truncate',
										isPendingZero ? 'text-muted-foreground/50' : 'text-foreground',
									)}
								>
									{formatCurrencyWithCents(asset.financeStats?.Pending?.amount)}
								</span>
								<span
									className="text-[10px] uppercase font-semibold text-muted-foreground mt-1"
									title={t('assets.card.packageCount')}
								>
									{t('assets.list.packages', { count: pendingCount })}
								</span>
							</div>
						</div>

						{/* Assigned Work Packages */}
						<div className="flex flex-col p-2.5 bg-card rounded-lg border border-border shadow-sm relative overflow-hidden group">
							<div
								className={cn(
									'absolute left-0 top-0 bottom-0 w-1',
									isAssignedZero ? 'bg-muted-foreground/30' : 'bg-emerald-500',
								)}
							/>
							<div className="pl-1.5 flex flex-col">
								<div className="flex justify-between items-center mb-1">
									<span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider flex items-center">
										<CheckIcon
											className={cn('h-3 w-3 mr-1', isAssignedZero ? 'text-muted-foreground/50' : 'text-emerald-500')}
										/>{' '}
										{t('assets.card.assignedWP')}
									</span>
								</div>
								<span
									className={cn(
										'text-sm font-extrabold truncate',
										isAssignedZero ? 'text-muted-foreground/50' : 'text-foreground',
									)}
								>
									{formatCurrencyWithCents(asset.financeStats?.Assigned?.amount)}
								</span>
								<span
									className="text-[10px] uppercase font-semibold text-muted-foreground mt-1"
									title={t('assets.card.packageCount')}
								>
									{t('assets.list.packages', { count: assignedCount })}
								</span>
							</div>
						</div>
					</div>
				</Restricted>

				<Restricted to={[ROLE.DAMAGES_READ, ROLE.INSPECTION_MANAGEMENT_PAGE_VIEW]}>
					<div className="mt-3">
						<div className="grid grid-cols-5 gap-2 text-center">
							<span className="text-[10px] text-muted-foreground font-medium flex items-center mb-2">
								{t('assets.card.activeDamages')}
							</span>
							<DamageStatBox
								label={t('assets.card.severity.crit')}
								count={asset.damageStats?.crit || 0}
								colorType="crit"
							/>
							<DamageStatBox
								label={t('assets.card.severity.high')}
								count={asset.damageStats?.high || 0}
								colorType="high"
							/>
							<DamageStatBox
								label={t('assets.card.severity.med')}
								count={asset.damageStats?.med || 0}
								colorType="med"
							/>
							<DamageStatBox
								label={t('assets.card.severity.low')}
								count={asset.damageStats?.low || 0}
								colorType="low"
							/>
						</div>
					</div>
				</Restricted>
			</CardContent>
		</Card>
	);
};
